/**
 * Building rendering. Boxes sized by the data `footprint`, tinted by faction
 * palette; construction sites render semi-transparent until complete.
 *
 * Models resolve through the ArtMap (GameDB.buildingModel) by building ID —
 * never by hardcoded paths. When the resolved model is vendored
 * (public/assets/manifest.json lists it), the GLB renders instead of the
 * placeholder box: scaled from the manifest `dimensions_and_origin` block
 * when present, else fitted to the data footprint from its measured bounds.
 * Unmapped or not-yet-vendored buildings keep the placeholder box.
 *
 * The view records an `artKey` (`<canonical_id>@band<N>` or
 * `placeholder:<id>`) so `refreshBuildingArt` can swap the visual when an
 * echelon advance changes the resolved band (the AoE town-transformation
 * moment). GLB loads are async: the placeholder shows immediately and is
 * replaced in place when the model arrives.
 *
 * CLONE RULE (see render/models.ts): the ModelStore vends a SHARED cached
 * scene — each view clones it and clones its materials (per-view
 * construction dimming). Shared geometry is never disposed by views.
 */
import * as THREE from "three";
import { footprintOf, type PlacedBuilding } from "../sim/buildings";
import { artKeyFor, unmappedModel, type ResolvedModel } from "../data/artmap";
import { ModelStore } from "./models";

export interface BuildingView {
  group: THREE.Group;
  /** Placeholder box (visible only while no GLB is attached). */
  mesh: THREE.Mesh;
  /** Fitted GLB clone, or null while the placeholder shows. */
  model: THREE.Group | null;
  building: PlacedBuilding;
  tint: number;
  /** Art-map key; a change means the visual must be rebuilt (band upgrade). */
  artKey: string;
}

function buildPlaceholderMesh(
  b: PlacedBuilding,
  tint: number,
  groundY: (x: number, z: number) => number,
): THREE.Mesh {
  const [w, d] = footprintOf(b.def);
  const h = Math.max(2.5, Math.min(w, d) * 0.55);
  const geo = new THREE.BoxGeometry(w, h, d);
  const mat = new THREE.MeshStandardMaterial({
    color: tint,
    roughness: 0.9,
    transparent: b.state !== "active",
    opacity: b.state === "active" ? 1 : 0.45,
  });
  const mesh = new THREE.Mesh(geo, mat);
  const gy = groundY(b.x, b.z);
  mesh.position.set(b.x, gy + h / 2, b.z);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * Uniform scale fitting a model of (sizeX, sizeY, sizeZ) metres into the
 * data footprint [footW, footD]. Height is capped at 2.5x the placeholder
 * height so a mis-scaled export can't become a skyscraper. Pure — tested.
 */
export function fitScaleForFootprint(
  sizeX: number,
  sizeY: number,
  sizeZ: number,
  footW: number,
  footD: number,
): number {
  const sx = sizeX > 1e-6 ? footW / sizeX : 1;
  const sz = sizeZ > 1e-6 ? footD / sizeZ : 1;
  let s = Math.min(sx, sz);
  const placeholderH = Math.max(2.5, Math.min(footW, footD) * 0.55);
  if (sizeY > 1e-6) s = Math.min(s, (placeholderH * 2.5) / sizeY);
  return Math.max(s, 1e-3);
}

/**
 * Fit a loaded GLB scene onto a building: clone per view (clone rule),
 * scale from the manifest dimensions block when present (else measured
 * bounds), ground-center origin. The art pipeline standardizes on -Z
 * forward (matching three.js), so no reorientation is applied.
 */
function fitBuildingModel(
  scene: THREE.Group,
  b: PlacedBuilding,
  model: ResolvedModel,
  groundY: (x: number, z: number) => number,
): THREE.Group {
  const holder = new THREE.Group();
  const inner = scene.clone(true);
  inner.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mat = mesh.material as THREE.Material | THREE.Material[];
    // Per-view material clones: construction dimming must not leak across
    // views sharing the store's cached scene.
    mesh.material = Array.isArray(mat) ? mat.map((m) => m.clone()) : mat.clone();
    mesh.castShadow = true;
    mesh.receiveShadow = true;
  });
  holder.add(inner);
  inner.updateMatrixWorld(true);
  const bbox = new THREE.Box3().setFromObject(inner);
  const size = new THREE.Vector3();
  bbox.getSize(size);
  const dimSize = model.dimensions?.sizeM;
  const s = fitScaleForFootprint(
    dimSize ? dimSize[0] : size.x,
    dimSize ? dimSize[1] : size.y,
    dimSize ? dimSize[2] : size.z,
    ...footprintOf(b.def),
  );
  inner.scale.setScalar(s);
  inner.updateMatrixWorld(true);
  const fitted = new THREE.Box3().setFromObject(inner);
  const center = new THREE.Vector3();
  fitted.getCenter(center);
  // Ground-center origin: min.y -> 0, x/z centered on the footprint.
  inner.position.x -= center.x;
  inner.position.z -= center.z;
  inner.position.y -= fitted.min.y;
  holder.position.set(b.x, groundY(b.x, b.z), b.z);
  return holder;
}

/** Dispose per-view material clones (shared geometry belongs to the store). */
function disposeModelGroup(holder: THREE.Group): void {
  holder.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mat = mesh.material as THREE.Material | THREE.Material[];
    if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
    else mat.dispose();
  });
}

function forEachModelMaterial(
  holder: THREE.Group,
  fn: (m: THREE.MeshStandardMaterial) => void,
): void {
  holder.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of mats) {
      if (m instanceof THREE.MeshStandardMaterial) fn(m);
    }
  });
}

export function createBuildingView(
  b: PlacedBuilding,
  tint: number,
  groundY: (x: number, z: number) => number,
  model?: ResolvedModel,
  store?: ModelStore | null,
): BuildingView {
  const mesh = buildPlaceholderMesh(b, tint, groundY);
  const group = new THREE.Group();
  group.name = b.def.id;
  group.userData.selectable = true;
  group.userData.label = b.def.name_en;
  group.userData.buildingUid = b.uid;
  group.add(mesh);
  const view: BuildingView = {
    group,
    mesh,
    model: null,
    building: b,
    tint,
    artKey: "",
  };
  resolveBuildingVisual(view, model, store ?? null, groundY);
  return view;
}

/**
 * (Re)resolve a building's visual: placeholder immediately, GLB swapped in
 * when its async load completes. Safe to call on echelon-up; a stale load
 * (artKey moved on) never paints.
 */
export function resolveBuildingVisual(
  view: BuildingView,
  model: ResolvedModel | undefined,
  store: ModelStore | null,
  groundY: (x: number, z: number) => number,
): void {
  const m = model ?? unmappedModel();
  const key = artKeyFor(m, view.building.def.id);
  view.artKey = key;
  if (view.model) {
    view.group.remove(view.model);
    disposeModelGroup(view.model);
    view.model = null;
  }
  if (!view.group.children.includes(view.mesh)) view.group.add(view.mesh);
  view.mesh.visible = true;
  updateBuildingView(view);
  if (!m.available || !store) return;
  void store.load(m).then((scene) => {
    if (!scene || view.artKey !== key) return; // stale or failed: keep placeholder
    try {
      const holder = fitBuildingModel(scene, view.building, m, groundY);
      view.group.remove(view.mesh);
      view.mesh.visible = false;
      view.group.add(holder);
      view.model = holder;
      updateBuildingView(view);
    } catch (e) {
      console.warn(`[buildings] failed to fit ${m.webPath}: ${String(e)}`);
    }
  });
}

/**
 * Re-resolve building models after an echelon advance; rebuild the visual
 * wherever the art key changed (echelon-band upgrade). Returns swap count.
 * GLB swaps complete asynchronously (placeholder first); the count reports
 * swaps initiated. Fog ghosting owns material dimming per frame — the
 * canonical state is restored via updateBuildingView after each swap.
 */
export function refreshBuildingArt(
  views: BuildingView[],
  resolve: (buildingId: string, echelon: number) => ResolvedModel,
  echelon: number,
  groundY: (x: number, z: number) => number,
  store?: ModelStore | null,
): number {
  let swaps = 0;
  for (const v of views) {
    const model = resolve(v.building.def.id, echelon);
    const key = artKeyFor(model, v.building.def.id);
    if (key === v.artKey) continue;
    // Placeholder is rebuilt inside; the GLB upgrade completes async.
    resolveBuildingVisual(v, model, store ?? null, groundY);
    swaps++;
  }
  return swaps;
}

/** Sync the visual with construction progress / completion. */
export function updateBuildingView(v: BuildingView): void {
  if (v.model) {
    const active = v.building.state === "active";
    forEachModelMaterial(v.model, (mat) => {
      mat.transparent = !active;
      mat.opacity = active ? 1 : 0.35 + v.building.progress * 0.35;
      if (active) mat.emissive.setHex(0x000000);
    });
    return;
  }
  const mat = v.mesh.material as THREE.MeshStandardMaterial;
  if (v.building.state === "active") {
    mat.transparent = false;
    mat.opacity = 1;
    mat.emissive.setHex(0x000000);
  } else {
    mat.transparent = true;
    // Pulse while under construction.
    mat.opacity = 0.35 + v.building.progress * 0.35;
  }
}

export function highlightBuilding(v: BuildingView, on: boolean): void {
  if (v.model) {
    forEachModelMaterial(v.model, (mat) => {
      if (v.building.state === "active") mat.emissive.setHex(on ? 0x553311 : 0x000000);
    });
    return;
  }
  const mat = v.mesh.material as THREE.MeshStandardMaterial;
  if (v.building.state === "active") {
    mat.emissive.setHex(on ? 0x553311 : 0x000000);
  }
}
